import {
  Box,
  Button,
  FooterActionBar,
  InlineMessage,
  InputField,
  Loader,
  MenuInput,
  MenuInputOption,
  ModalIsland,
  Typography,
} from "@saviynt/design-system";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import CreateUserSuccessModal from "./CreateUserSuccessModal";
import { useCreateUser } from "./hooks/useCreateUser";
import { useGetRbacRoles } from "./hooks/useGetRoles";
import { getRoleDisplayName } from "../../utils/roleDisplayName";
import { ApiError, type RoleResponse } from "@repo/api";

/** Pragmatic email shape check (not full RFC 5322). */
const EMAIL_FORMAT_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmailFormat(value: string): boolean {
  return EMAIL_FORMAT_REGEX.test(value.trim());
}

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Extracts a user-facing error message from the mutation error.
 * Handles known API error scenarios:
 * - 409: User already exists
 * - 403/429: User limit reached
 * - 500+: Generic server error
 */
function getCreateUserErrorMessage(
  error: Error | null,
  intl: ReturnType<typeof useIntl>,
): string | null {
  if (!error) return null;

  if (error instanceof ApiError) {
    // For 500+ server errors, always show a user-friendly client-side message
    // (backend just returns generic "An unexpected error occurred")
    if (error.status >= 500) {
      return intl.formatMessage({
        id: "tLQnzv",
        defaultMessage:
          "Something went wrong on our end. Please try again later or contact support if the issue persists.",
        description:
          "Error message when user creation fails due to a server error",
      });
    }

    // For non-500 errors, check for server-provided message in the response body
    // Response format: { error: "ERROR_CODE", message: "Human-readable message", status, timestamp }
    const responseData = error.data;
    const serverMessage =
      typeof responseData === "object" &&
      responseData !== null &&
      "message" in responseData &&
      typeof (responseData as { message: unknown }).message === "string"
        ? (responseData as { message: string }).message
        : null;

    // If the server provided a message, use it directly
    if (serverMessage && serverMessage.length > 0) {
      return serverMessage;
    }

    // Fallback to status-based messages when no server message is available
    if (error.status === 409) {
      return intl.formatMessage({
        id: "XKGczW",
        defaultMessage:
          "A user with this email already exists. Please use a different email address.",
        description:
          "Error message when user creation fails because user already exists",
      });
    }

    if (error.status === 403 || error.status === 429) {
      return intl.formatMessage({
        id: "e30VdF",
        defaultMessage:
          "User limit has been reached for your organization. Please contact support.",
        description: "Error message when user creation fails due to user limit",
      });
    }
  }

  // Generic fallback for non-ApiError failures (network issues, etc.)
  return intl.formatMessage({
    id: "t3V+r1",
    defaultMessage:
      "Unable to create user at the moment. Please try again or contact support.",
    description: "Generic error message when user creation fails",
  });
}

export default function CreateUserModal({
  isOpen,
  onClose,
}: CreateUserModalProps) {
  const intl = useIntl();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdUserEmail, setCreatedUserEmail] = useState("");

  const createUserMutation = useCreateUser();

  const { data: rbacRoles, isLoading, isError } = useGetRbacRoles();

  const errorMessage = useMemo(
    () => getCreateUserErrorMessage(createUserMutation.error, intl),
    [createUserMutation.error, intl],
  );

  const [savRoles, setSavRoles] = useState<MenuInputOption[]>([]);

  useEffect(() => {
    if (!isLoading) {
      if (isError) {
        setSavRoles([]);
      } else {
        const roles = [...(rbacRoles ?? [])]?.map((role: RoleResponse) => ({
          value: role.roleKey,
          label: getRoleDisplayName(role.roleName),
          isChecked: false,
        }));
        setSavRoles(roles);
      }
    }
  }, [rbacRoles, isLoading, isError]);

  useEffect(() => {
    if (!isOpen) {
      setShowSuccess(false);
      setCreatedUserEmail("");
      setFirstName("");
      setLastName("");
      setEmail("");
      createUserMutation.reset();
    }
  }, [isOpen]);

  const handleChange = useCallback((opt: MenuInputOption) => {
    setSavRoles((prev) => {
      return prev.map((role) => ({
        ...role,
        isChecked: role.value === opt.value,
      }));
    });
  }, []);

  const handleSubmit = useCallback(() => {
    const payload = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      roleKey:
        savRoles.find((role) => role.isChecked)?.value ?? "ROLE_SAV_ISPMVIEWER",
    };

    createUserMutation.mutate(payload, {
      onSuccess: () => {
        setCreatedUserEmail(payload.email);
        setShowSuccess(true);
      },
    });
  }, [firstName, lastName, email, savRoles]);

  const handleCloseCreate = () => {
    onClose();
  };

  const handleSuccessDone = () => {
    setShowSuccess(false);
    setCreatedUserEmail("");
    onClose();
  };

  const isEmailFieldCritical = useMemo(
    () => email.length > 0 && !isValidEmailFormat(email),
    [email],
  );

  const isDisabled = useMemo(() => {
    return (
      !firstName.trim() ||
      !lastName.trim() ||
      !email.trim() ||
      !isValidEmailFormat(email) ||
      createUserMutation.isPending
    );
  }, [firstName, lastName, email, savRoles, createUserMutation.isPending]);

  if (showSuccess) {
    return (
      <CreateUserSuccessModal
        isOpen={isOpen && showSuccess}
        email={createdUserEmail}
        onDone={handleSuccessDone}
      />
    );
  }

  return (
    <>
      <ModalIsland
        isOpen={isOpen && !showSuccess}
        onClose={handleCloseCreate}
        title={intl.formatMessage({
          id: "5mow+d",
          defaultMessage: "Create New User for Zuma",
          description: "Create New User for Zuma modal title",
        })}
        contentBody={
          <>
            {errorMessage && (
              <Box className="mb-4">
                <InlineMessage
                  kind="background"
                  size="medium"
                  colorTheme="critical"
                  text={errorMessage}
                  isDismissible
                  onDismiss={() => createUserMutation.reset()}
                  dataTestId="CreateUserModal-errorMessage"
                />
              </Box>
            )}
            <Box className="flex justify-between gap-4">
              <InputField
                name="firstName"
                type="text"
                placeholder={intl.formatMessage({
                  id: "t9k04V",
                  defaultMessage: "First Name",
                  description: "Email input placeholder",
                })}
                label={
                  <Typography kind="label">
                    {intl.formatMessage({
                      id: "S9+idf",
                      defaultMessage: "First Name",
                      description: "First Name input label",
                    })}
                  </Typography>
                }
                value={firstName}
                setValue={(val) => setFirstName(val)}
                isRequired
              />
              <InputField
                name="lastName"
                type="text"
                placeholder={intl.formatMessage({
                  id: "79781u",
                  defaultMessage: "Last Name",
                  description: "Last Name input placeholder",
                })}
                label={
                  <Typography kind="label">
                    {intl.formatMessage({
                      id: "Ez4bNm",
                      defaultMessage: "Last Name",
                      description: "Last Name input label",
                    })}
                  </Typography>
                }
                value={lastName}
                setValue={(val) => setLastName(val)}
                isRequired
              />
            </Box>
            <InputField
              name="email"
              type="email"
              placeholder={intl.formatMessage({
                id: "WaLa6x",
                defaultMessage: "Email",
                description: "Email input placeholder",
              })}
              label={
                <Typography kind="label">
                  {intl.formatMessage({
                    id: "FTNl8q",
                    defaultMessage: "Email",
                    description: "Email input label",
                  })}
                </Typography>
              }
              value={email}
              setValue={(val) => setEmail(val)}
              isRequired
              isCritical={isEmailFieldCritical}
              CriticalHelperText={
                isEmailFieldCritical ? (
                  <FormattedMessage
                    id="UPqA/Q"
                    defaultMessage="Enter a valid email address."
                    description="Email validation error in add user form"
                  />
                ) : undefined
              }
            />
            <MenuInput
              label="items-with-leading-icon"
              placeholder="Select"
              options={savRoles}
              isLoading={isLoading}
              isRequired
              onChange={(opt) => handleChange(opt)}
              inputLabel={
                <Typography kind="label">
                  {intl.formatMessage({
                    id: "6Ra8A5",
                    defaultMessage: "Assign Role",
                    description: "Roles input label",
                  })}
                </Typography>
              }
            />
          </>
        }
        FooterActionBarComp={
          <FooterActionBar size="large">
            <Box className="flex justify-end gap-2 w-full">
              <Button
                kind="filled"
                size="medium"
                isDisabled={isDisabled}
                isLoading={createUserMutation.isPending}
                LoaderComp={
                  <Loader kind="loop" format="inline" color="brand" />
                }
                loaderText={intl.formatMessage({
                  id: "a4Xf7m",
                  defaultMessage: "Creating User...",
                  description: "Loading text while creating user",
                })}
                onClick={handleSubmit}
              >
                <FormattedMessage
                  id="HjVEZ6"
                  defaultMessage="Create User"
                  description="Create User button"
                />
              </Button>
            </Box>
          </FooterActionBar>
        }
      />
    </>
  );
}
